import React from 'react';
import { TextField, useTheme, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

const StyledTextField = ({
  textFieldSx = {},
  labelSx = {},
  inputSx = {},
  InputLabelProps: inputLabelPropsFromOutside = {},
  InputProps: inputPropsFromOutside = {},
  formik,
  name,
  value,
  onChange,
  onBlur,
  id,
  isPrimaryBorder = false,
  focusBorderPrimary = false,
  height = '45px',
  minHeight = '45px',
  showEndAdornment = false,
  onEndAdornmentClick,
  externalError = false,
  helperText: helperTextOverride,
  ...props
}) => {
  const theme = useTheme();
  const borderColorDefault = isPrimaryBorder ? theme.primary : theme.tertiary;
  const borderColorHover = isPrimaryBorder ? theme.tertiary : theme.primary;

  const fieldId = id || name;

  const isFileInput = props.type === 'file';

  let fieldProps = {
    ...(isFileInput ? {} : { value: value ?? '' }),
    value: value ?? '',
    onChange,
    onBlur,
    error: !!externalError,
    helperText: helperTextOverride || '',
  };

  if (formik && name) {
    const field = formik.getFieldProps(name);
    const meta = formik.getFieldMeta(name);
    const hasError = Boolean(meta.touched && meta.error);
    fieldProps = {
      ...(isFileInput ? {} : { value: field.value ?? '' }),
      onChange: e => {
        if (onChange) {
          onChange(e);
        } else {
          field.onChange(e);
        }
      },
      onBlur: e => {
        if (onBlur) onBlur(e);
        field.onBlur(e);
      },
      error: hasError || !!externalError,
      helperText:
        (hasError && meta.error) ||
        (externalError && (helperTextOverride || '')) ||
        helperTextOverride ||
        '',
    };
  }

  const endAdornment = showEndAdornment ? (
    <InputAdornment position="end">
      <IconButton size="small" onClick={onEndAdornmentClick} tabIndex={-1}>
        <ClearIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ) : null;

  const isMultiline = props.multiline;

  return (
    <TextField
      id={fieldId}
      autoComplete="off"
      fullWidth
      {...fieldProps}
      {...props}
      InputLabelProps={{
        shrink: true,
        sx: {
          fontSize: '16px',
          color: theme.textGray,
          ...labelSx,
          ...inputLabelPropsFromOutside?.sx,
        },
        ...inputLabelPropsFromOutside,
      }}
      InputProps={{
        sx: {
          fontSize: '16px',
          color: '#000',

          // 👇 estilos distintos según sea multiline o no
          ...(isMultiline && {
            alignItems: 'flex-start',
            height: 'auto',
            minHeight: 'auto',
            paddingTop: '8px',
          }),

          ...(!isMultiline && {
            height,
            minHeight,
            display: 'flex',
            alignItems: 'center',
          }),

          boxSizing: 'border-box',
          '& .MuiInputAdornment-root': { flexShrink: 0 },
          ...inputSx,
          ...inputPropsFromOutside?.sx,
        },
        ...inputPropsFromOutside,
        endAdornment: endAdornment || inputPropsFromOutside?.endAdornment,
      }}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: focusBorderPrimary
              ? borderColorDefault
              : theme.palette.grey[400],
          },
          '&:hover fieldset': {
            borderColor: focusBorderPrimary
              ? theme.palette.grey[600]
              : borderColorHover,
          },
          '&.Mui-focused fieldset': {
            borderColor: focusBorderPrimary ? theme.primary : borderColorHover,
            borderWidth: focusBorderPrimary ? '2px' : undefined,
          },
        },
        ...textFieldSx,
      }}
    />
  );
};

export default StyledTextField;
